import { IconButton } from "@hortis/ui/button";
import { formatDate } from "@hortis/ui/date-picker";
import { Dot } from "@hortis/ui/dot";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@hortis/ui/hover-card";
import {
  BezierCurve2,
  CalendarMinus2,
  CoinsStacked3,
  Edit2,
  IntersectCircle,
  Map2,
  MarkerPin4,
  Scales1,
  Tag1,
  Tag3,
  TagNumber,
} from "@hortis/ui/icons";
import { CalendarPlus2 } from "@hortis/ui/icons/calendar-plus-2";
import { MarkerPin1 } from "@hortis/ui/icons/marker-pin-1";
import { Tag } from "@hortis/ui/tag";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { MaterialFieldsFragment } from "generated/graphql";
import { useState } from "react";
import TimeAgo, { Formatter } from "react-timeago";
import {
  MobileProperty,
  RecordProperties,
  RecordPropertiesGroup,
  RecordPropertiesGroupTitle,
  RecordPropertiesItem,
  RecordPropertiesItemLabel,
  RecordPropertiesItemValue,
  ViewProperty,
} from "src/components/record-properties/record-properties";
import {
  PlantMaterialStatusToColor,
  PlantMaterialStatusToLabel,
} from "src/components/status-chip";
import { parseUtcDate } from "src/utils/dates/parse-utc-date";
import { materialGroupLabelMap } from "src/utils/label-maps/material-group";
import { materialSexLabelMap } from "src/utils/label-maps/material-sex";
import { twMerge } from "tailwind-merge";

const timeAgoFormatter: Formatter = (
  value,
  unit,
  suffix,
  epochMilliseconds,
  nextFormatter,
) =>
  unit === "second" || unit === "minute" || unit === "hour"
    ? "Today"
    : nextFormatter == null
      ? undefined
      : nextFormatter(value, unit, suffix, epochMilliseconds);

export const MaterialViewProperties = ({
  material,
  className,
}: {
  material: NonNullable<MaterialFieldsFragment>;
  className?: string;
}) => {
  const tags = material.tagsConnection?.nodes ?? [];
  return (
    <div className={twMerge("flex flex-wrap gap-x-3 gap-y-1.5", className)}>
      {material.firstPresent != null && (
        <Tooltip>
          <TooltipTrigger>
            <ViewProperty>
              <CalendarPlus2 className="h-4 w-4" />
              <TimeAgo
                date={material.firstPresent}
                formatter={timeAgoFormatter}
                title=""
              />
            </ViewProperty>
          </TooltipTrigger>
          <TooltipContent>
            Added on {formatDate(parseUtcDate(material.firstPresent))}
          </TooltipContent>
        </Tooltip>
      )}
      {material.firstAbsent != null && (
        <ViewProperty>
          <CalendarMinus2 className="h-4 w-4" />
          {formatDate(parseUtcDate(material.firstAbsent))}
        </ViewProperty>
      )}
      {material.materialGroup != null && (
        <ViewProperty>
          <Tag1 className="h-4 w-4" />
          {materialGroupLabelMap[material.materialGroup]}
        </ViewProperty>
      )}
      {material.status != null && (
        <ViewProperty>
          <Dot color={PlantMaterialStatusToColor[material.status]} />
          {PlantMaterialStatusToLabel[material.status]}
        </ViewProperty>
      )}
      {material.collectionSiteLocation != null && (
        <ViewProperty>
          <Map2 className="h-4 w-4" />
          <span className="max-w-24 truncate">
            {material.collectionSiteLocation.name}
          </span>
          <span className="text-grey-500 max-w-24 truncate">
            {material.collectionSiteLocation.description}
          </span>
        </ViewProperty>
      )}
      {material.position != null && (
        <ViewProperty>
          <MarkerPin1 className="h-4 w-4" />
          {material.position.latitude.toFixed(3)},{" "}
          {material.position.longitude.toFixed(3)}
        </ViewProperty>
      )}
      {material.sex != null && (
        <ViewProperty>
          <IntersectCircle className="h-4 w-4" />
          {materialSexLabelMap[material.sex]}
        </ViewProperty>
      )}
      {material.tagNumber != null && (
        <ViewProperty>
          <TagNumber className="h-4 w-4" />
          {material.tagNumber}
        </ViewProperty>
      )}
      {material.quantity != null && (
        <ViewProperty>
          <CoinsStacked3 className="h-4 w-4" />
          {material.quantity} total
        </ViewProperty>
      )}
      {material.weight != null && (
        <ViewProperty>
          <Scales1 className="h-4 w-4" />
          {material.weight} grams
        </ViewProperty>
      )}
      {material.massPlanting && (
        <ViewProperty>
          <BezierCurve2 className="h-4 w-4" />
          Mass planting
        </ViewProperty>
      )}
      {tags.length > 0 && (
        <HoverCard>
          <HoverCardTrigger className="cursor-pointer">
            <ViewProperty>
              <Tag3 className="h-4 w-4" />
              {tags.length > 2
                ? `${tags.length} tags`
                : tags.map(({ name }) => name).join(", ")}
            </ViewProperty>
          </HoverCardTrigger>
          <HoverCardContent className="p-3 space-y-1.5 max-w-xs" align="start">
            <div className="font-medium text-sm text-grey-500">Tags</div>
            <div className="flex flex-wrap items-center gap-1">
              {tags.map(({ id, name }) => (
                <Tag
                  key={id}
                  data-cy="taxon-tag"
                  size="lg"
                  className="px-1.5 py-0.5"
                >
                  {name}
                </Tag>
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
      )}
    </div>
  );
};

export const MaterialMobileProperties = ({
  material,
  onRequestEdit,
  canEdit,
  className,
  onRequestResposition,
}: {
  material: NonNullable<MaterialFieldsFragment>;
  onRequestEdit: () => void;
  canEdit: boolean;
  className?: string;
  onRequestResposition?: () => void;
}) => {
  const tags = material.tagsConnection?.nodes ?? [];
  return (
    <div className={twMerge("space-y-1 pb-1", className)}>
      <div className="flex flex-wrap gap-1">
        {material.firstPresent != null && (
          <Tooltip>
            <TooltipTrigger>
              <MobileProperty data-cy="first-present-picker">
                <CalendarPlus2 className="h-4 w-4" />
                <TimeAgo
                  date={material.firstPresent}
                  formatter={timeAgoFormatter}
                  title=""
                />
              </MobileProperty>
            </TooltipTrigger>
            <TooltipContent>
              Added on {formatDate(parseUtcDate(material.firstPresent))}
            </TooltipContent>
          </Tooltip>
        )}
        {material.firstAbsent != null && (
          <MobileProperty data-cy="first-absent-picker">
            <CalendarMinus2 className="h-4 w-4" />
            {formatDate(parseUtcDate(material.firstAbsent))}
          </MobileProperty>
        )}
        {material.materialGroup != null && (
          <MobileProperty data-cy="material-group-picker">
            <Tag1 className="h-4 w-4" />
            {materialGroupLabelMap[material.materialGroup]}
          </MobileProperty>
        )}
        {material.status != null && (
          <MobileProperty data-cy="status-picker">
            <Dot color={PlantMaterialStatusToColor[material.status]} />
            {PlantMaterialStatusToLabel[material.status]}
          </MobileProperty>
        )}
        {material.collectionSiteLocation != null && (
          <MobileProperty data-cy="location-picker">
            <Map2 className="h-4 w-4" />
            <span className="max-w-24 truncate">
              {material.collectionSiteLocation.name}
            </span>
            <span className="text-grey-500 max-w-24 truncate">
              {material.collectionSiteLocation.description}
            </span>
          </MobileProperty>
        )}
        {material.position != null && (
          <MobileProperty data-cy="position-picker">
            <MarkerPin1 className="h-4 w-4" />
            {material.position.latitude.toFixed(3)},{" "}
            {material.position.longitude.toFixed(3)}
          </MobileProperty>
        )}
        {material.sex != null && (
          <MobileProperty data-cy="sex-picker">
            <IntersectCircle className="h-4 w-4" />
            {materialSexLabelMap[material.sex]}
          </MobileProperty>
        )}
        {material.tagNumber != null && (
          <MobileProperty data-cy="tag-number-picker">
            <TagNumber className="h-4 w-4" />
            {material.tagNumber}
          </MobileProperty>
        )}
        {material.quantity != null && (
          <MobileProperty data-cy="quantity-picker">
            <CoinsStacked3 className="h-4 w-4" />
            {material.quantity} total
          </MobileProperty>
        )}
        {material.weight != null && (
          <MobileProperty data-cy="weight-picker">
            <Scales1 className="h-4 w-4" />
            {material.weight} grams
          </MobileProperty>
        )}
        {material.massPlanting && (
          <MobileProperty data-cy="mass-planting-picker">
            <BezierCurve2 className="h-4 w-4" />
            Mass planting
          </MobileProperty>
        )}
        {tags.length > 0 && (
          <HoverCard>
            <HoverCardTrigger className="cursor-pointer">
              <MobileProperty data-cy="tags-picker">
                <Tag3 className="h-4 w-4" />
                {tags.length > 2
                  ? `${tags.length} tags`
                  : tags.map(({ name }) => name).join(", ")}
              </MobileProperty>
            </HoverCardTrigger>
            <HoverCardContent
              className="p-3 space-y-1.5 max-w-xs"
              align="start"
            >
              <div className="font-medium text-sm text-grey-500">Tags</div>
              <div className="flex flex-wrap items-center gap-1">
                {tags.map(({ id, name }) => (
                  <Tag
                    key={id}
                    data-cy="taxon-tag"
                    size="lg"
                    className="px-1.5 py-0.5"
                  >
                    {name}
                  </Tag>
                ))}
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
        <div className="flex items-center gap-1">
          {canEdit && !material.archived && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  onClick={onRequestEdit}
                  ariaLabel="Edit section"
                  data-cy="properties-edit-button"
                  size="xs"
                  variant="tertiaryGray"
                  className="bg-grey-50 rounded-full p-1.5 hover:bg-grey-100 border border-grey-100"
                  icon={Edit2}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">Edit properties</TooltipContent>
            </Tooltip>
          )}
          {canEdit && !material.archived && onRequestResposition != null && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  onClick={onRequestResposition}
                  ariaLabel="Edit section"
                  data-cy="update-position-button"
                  size="xs"
                  variant="tertiaryGray"
                  className="bg-grey-50 rounded-full p-1.5 hover:bg-grey-100 border border-grey-100"
                  icon={MarkerPin4}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">Change position</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export const MaterialProperties = ({
  material,
  canEdit,
  onRequestEdit,
  className,
}: {
  material: NonNullable<MaterialFieldsFragment>;
  onRequestEdit: () => void;
  canEdit: boolean;
  className?: string;
}) => {
  const [taxonExpanded, setTaxonExpanded] = useState(false);
  const [accessionExpanded, setAccessionExpanded] = useState(false);
  return (
    <RecordProperties className={twMerge("", className)}>
      <RecordPropertiesGroup>
        <RecordPropertiesGroupTitle>
          Properties
          {canEdit && !material.archived && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  onClick={onRequestEdit}
                  ariaLabel="Edit section"
                  data-cy="properties-edit-button"
                  size="sm"
                  variant="tertiaryGray"
                  icon={Edit2}
                  className="hover:bg-grey-100 absolute -right-2 p-1.5"
                />
              </TooltipTrigger>
              <TooltipContent side="left">Edit material</TooltipContent>
            </Tooltip>
          )}
        </RecordPropertiesGroupTitle>
        {material.firstPresent != null && (
          <RecordPropertiesItem data-cy="first-present-picker">
            <RecordPropertiesItemLabel>Date added</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {formatDate(parseUtcDate(material.firstPresent))}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {material.firstAbsent != null && (
          <RecordPropertiesItem data-cy="first-absent-picker">
            <RecordPropertiesItemLabel>Date removed</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {formatDate(parseUtcDate(material.firstAbsent))}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        <RecordPropertiesItem data-cy="status-picker">
          <RecordPropertiesItemLabel>Status</RecordPropertiesItemLabel>
          <RecordPropertiesItemValue>
            <Dot color={PlantMaterialStatusToColor[material.status]} />
            {PlantMaterialStatusToLabel[material.status]}
          </RecordPropertiesItemValue>
        </RecordPropertiesItem>
        <RecordPropertiesItem data-cy="material-type-picker">
          <RecordPropertiesItemLabel>Type</RecordPropertiesItemLabel>
          <RecordPropertiesItemValue>
            {materialGroupLabelMap[material.materialGroup]}
          </RecordPropertiesItemValue>
        </RecordPropertiesItem>
        {material.collectionSiteLocation != null && (
          <RecordPropertiesItem data-cy="location-picker">
            <RecordPropertiesItemLabel>Location</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue className="inline">
              <span className="inline">
                {material.collectionSiteLocation.name}
              </span>
              <span className="text-grey-500">
                {" "}
                {material.collectionSiteLocation.description}
              </span>
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {material.sex != null && (
          <RecordPropertiesItem data-cy="sex-picker">
            <RecordPropertiesItemLabel>Sex</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {materialSexLabelMap[material.sex]}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {material.tagNumber != null && (
          <RecordPropertiesItem data-cy="tag-number-picker">
            <RecordPropertiesItemLabel>Tag number</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {material.tagNumber}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {(material.quantity != null || material.massPlanting) && (
          <RecordPropertiesItem data-cy="quantity-picker">
            <RecordPropertiesItemLabel>Quantity</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {material.quantity != null
                ? material.quantity
                : material.massPlanting
                  ? "Mass planting"
                  : null}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {material.weight != null && (
          <RecordPropertiesItem data-cy="weight-picker">
            <RecordPropertiesItemLabel>Weight</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {material.weight} grams
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {material.tagsConnection?.nodes != null &&
          material.tagsConnection.nodes.length > 0 && (
            <RecordPropertiesItem className="gap-2" data-cy="tags-picker">
              <RecordPropertiesItemLabel>Tags</RecordPropertiesItemLabel>
              <RecordPropertiesItemValue className="flex flex-wrap gap-1.5">
                {material.tagsConnection.nodes.map(({ id, name }) => (
                  <Tag
                    key={id}
                    data-cy="taxon-tag"
                    size="lg"
                    className="px-1.5 py-0.5"
                  >
                    {name}
                  </Tag>
                ))}
              </RecordPropertiesItemValue>
            </RecordPropertiesItem>
          )}
      </RecordPropertiesGroup>
    </RecordProperties>
  );
};
